/**
 * filterUtils.js — Frontend utilities for building filter, sort, and pagination
 * request bodies for the Phase 2 v3 APIs (getchecklist, getglaccounts, gettrialbalance).
 *
 * These mirror the backend helpers in utils/filterUtils.js (Supernova) to
 * ensure request/response shape consistency.
 */

/**
 * Filter operator constants
 */
export const FILTER_OPERATORS = {
  EQUALS: 'equals',
  NOT_EQUALS: 'not_equals',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
  STARTS_WITH: 'starts_with',
  ENDS_WITH: 'ends_with',
  GREATER_THAN: 'gt',
  LESS_THAN: 'lt',
  GREATER_EQUAL: 'gte',
  LESS_EQUAL: 'lte',
  IN: 'in',
  NOT_IN: 'not_in',
  IS_EMPTY: 'is_empty',
  IS_NOT_EMPTY: 'is_not_empty',
};

/**
 * Group connector constants
 */
export const GROUP_CONNECTORS = {
  AND: 'AND',
  OR: 'OR',
};

/**
 * Build a filter request body for the v3 APIs.
 * @param {Array} conditions - Array of { field, operator, value }
 * @param {string} connector - 'AND' or 'OR' (default: 'AND')
 * @returns {Object|null} Filters object for API body, or null if empty
 */
export function buildFilterBody(conditions = [], connector = 'AND') {
  if (!conditions || conditions.length === 0) return null;
  return {
    connector,
    groups: [
      {
        connector,
        conditions: conditions.filter(c => c && c.field),
      },
    ],
  };
}

/**
 * Build a sort request body for the v3 APIs.
 * @param {string} field - Field to sort by
 * @param {string} direction - 'asc' or 'desc' (default: 'asc')
 * @returns {Array} Sort array for API body
 */
export function buildSortBody(field, direction = 'asc') {
  if (!field) return [];
  return [{ field, direction }];
}

/**
 * Build a pagination request body for the v3 APIs.
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Items per page
 * @returns {Object} Pagination object for API body
 */
export function buildPaginationBody(page = 1, limit = 50) {
  return { page, limit };
}

/**
 * Build a search filter for the TB endpoint.
 * The backend searches across glcode, name, reference_detail.ref, location_detail.name.
 * @param {string} searchText - Raw search text from user input
 * @returns {string|null} Search text to pass to API, or null if empty
 */
export function buildTbSearchParam(searchText) {
  const trimmed = (searchText || '').trim();
  return trimmed || null;
}

/**
 * Field type map for checklist filtering (for future server-side filtering).
 * Maps field names to their data types for proper value coercion.
 */
export const CHECKLIST_FIELD_TYPE_MAP = {
  duedate: 'date',
  stage: 'number',
  location: 'objectId',
  preparer: 'objectIdArray',
  approver: 'objectIdArray',
  description: 'string',
  category: 'string',
  subcategory: 'string',
  task_type: 'string',
  priority: 'string',
  '__computed.locationName': 'string',
  '__computed.tbtotals.total': 'number',
};

/**
 * Field type map for trial balance filtering.
 */
export const TB_FIELD_TYPE_MAP = {
  glcode: 'string',
  name: 'string',
  closing_amount: 'number',
  'reference_detail.ref': 'string',
  'location_detail.name': 'string',
};
